import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { Activity, AlertTriangle, ClipboardList, Compass, FileSearch, MessageCircle, Sparkles } from "lucide-react";

const Icons = { Activity, AlertTriangle, ClipboardList, Compass, FileSearch, MessageCircle, Sparkles };

export default function ForIndividualsPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="For Individuals" description="Guided psychological self-reflection and assessment support for individuals." />
      <div className="pt-20">
      <PageHero
        eyebrow="For Individuals"
        title="Understand Yourself More Clearly"
        subtitle="PsyTech helps individuals explore structured psychological self-reflection and assessment support in a clear, guided way."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Guided assessments</Pill>
          <Pill tone="cyan">Structured summaries</Pill>
          <Pill tone="cyan">Professional-friendly output</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="What you can do" title="A guided way to reflect" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.ClipboardList} title="Complete guided assessments">Structured flows that walk you through questions in a calm, clear way.</FeatureCard>
          <FeatureCard icon={Icons.Sparkles} title="Receive structured summaries">Clean, professional summaries of your responses that are easy to read back.</FeatureCard>
          <FeatureCard icon={Icons.FileSearch} title="Understand patterns in responses">See common themes across your answers, not just individual items.</FeatureCard>
          <FeatureCard icon={Icons.MessageCircle} title="Prepare for a conversation with a professional">Bring a clear starting point into a session with your psychologist or therapist.</FeatureCard>
          <FeatureCard icon={Icons.Activity} title="Track assessment history">Where available, keep track of structured assessments over time.</FeatureCard>
          <FeatureCard icon={Icons.Compass} title="Learn about the process">Understand what the platform does — and does not — do, in plain language.</FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Important boundaries" title="What PsyTech is not for you" />
        <div className="grid md:grid-cols-2 gap-3">
          {[
            "Not a diagnosis",
            "Not therapy",
            "Not medication advice",
            "Not emergency support",
            "Please speak with a qualified professional for clinical concerns",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <CtaBlock
        title="Try the PsyTech platform"
        subtitle="Open the PsyTech platform to start a guided assessment. It takes just a few minutes."
        primaryHref="https://psytech-platform.on-forge.com/en/login"
        primaryLabel="Open PsyTech platform"
        secondaryHref="/trust-safety"
        secondaryLabel="Read how we approach safety"
        testid="for-individuals-cta"
      />
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
